import { useState } from "react";
import {
  NavLink,
  useNavigate,
} from "react-router-dom";

import {
  modules,
  futureModules,
} from "@/modules/registry";

import { useAuth } from "@/context/AuthContext";
import { useFarm } from "@/context/FarmContext";

import { Button } from "@/components/ui/button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Menu,
  X,
  LogOut,
  Leaf,
  Lock,
  Plus,
  BriefcaseBusiness,
} from "lucide-react";


export function Layout({ children }) {
  const [open, setOpen] =
    useState(false);

  const {
    user,
    logout,
  } = useAuth();

  const {
    farms,
    farm,
    farmId,
    selectFarm,
    isClientFarm,
  } = useFarm();

  const navigate =
    useNavigate();


  const isAdmin =
    user?.role === "admin";

  const isConsultant =
    user?.subscription_plan === "advisor" ||
    user?.subscription_plan === "consultant";


  const visibleModules =
    modules.filter(
      (module) => {
        if (
          module.adminOnly &&
          !isAdmin
        ) {
          return false;
        }

        if (
          module.consultantOnly &&
          !isAdmin &&
          !isConsultant
        ) {
          return false;
        }

        return true;
      }
    );


  const grouped = {};

  visibleModules.forEach(
    (module) => {
      (
        grouped[module.group] ||=
          []
      ).push(module);
    }
  );


  const nav = (
    <div className="flex h-full flex-col">

      {/* Brand */}

      <div className="flex items-center gap-2 px-6 py-5 border-b border-border">

        <div className="grid place-items-center h-9 w-9 rounded-lg bg-primary text-primary-foreground">

          <Leaf className="h-5 w-5" />

        </div>


        <div>

          <p className="font-display font-extrabold text-lg leading-none tracking-tight text-[hsl(148,38%,16%)]">
            Farm Hand
          </p>

          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">
            Grassland & Feed
          </p>

        </div>

      </div>


      {/* Navigation */}

      <nav
        className="flex-1 overflow-y-auto px-3 py-4 space-y-5"
        data-testid="sidebar-nav"
      >

        {Object.entries(
          grouped
        ).map(
          ([
            group,
            mods,
          ]) => (

            <div key={group}>

              <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {group}
              </p>


              {mods.map(
                (module) => (

                  <NavLink
                    key={
                      module.key
                    }
                    to={
                      module.path
                    }
                    end={
                      module.path ===
                      "/app"
                    }
                    onClick={() =>
                      setOpen(false)
                    }
                    data-testid={`nav-${module.key}`}
                    className={({
                      isActive,
                    }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-[hsl(148,38%,16%)] hover:bg-secondary"
                      }`
                    }
                  >

                    <module.icon className="h-4 w-4 shrink-0" />

                    {module.title}

                  </NavLink>

                )
              )}

            </div>

          )
        )}


        {/* Coming soon */}

        <div>

          <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Coming Soon
          </p>


          {futureModules.map(
            (module) => (

              <NavLink
                key={
                  module.key
                }
                to={`/app/soon/${module.key}`}
                onClick={() =>
                  setOpen(false)
                }
                data-testid={`nav-soon-${module.key}`}
                className={({
                  isActive,
                }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60"
                  }`
                }
              >

                <module.icon className="h-4 w-4 shrink-0" />

                <span className="flex-1">
                  {module.title}
                </span>

                <Lock className="h-3 w-3 opacity-60" />

              </NavLink>

            )
          )}

        </div>

      </nav>


      {/* User */}

      <div className="border-t border-border p-4">

        <p className="text-sm font-medium text-[hsl(148,38%,16%)] truncate">
          {user?.name}
        </p>

        <p className="text-xs text-muted-foreground truncate mb-3">
          {user?.email}
        </p>


        <Button
          variant="outline"
          size="sm"
          className="w-full rounded-full"
          data-testid="logout-button"
          onClick={() => {
            logout();

            navigate(
              "/login"
            );
          }}
        >

          <LogOut className="h-4 w-4 mr-2" />

          Sign out

        </Button>

      </div>

    </div>
  );


  return (
    <div className="min-h-screen grain flex">

      {/* Desktop sidebar */}

      <aside className="hidden lg:block w-72 shrink-0 bg-card border-r border-border sticky top-0 h-screen">

        {nav}

      </aside>


      {/* Mobile drawer */}

      {open && (

        <div className="fixed inset-0 z-50 lg:hidden">

          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() =>
              setOpen(false)
            }
          />

          <div className="absolute left-0 top-0 h-full w-72 bg-card shadow-xl">

            {nav}

          </div>

        </div>

      )}


      <div className="flex-1 min-w-0 flex flex-col">

        {/* Header */}

        <header className="sticky top-0 z-40 border-b border-border bg-white/70 backdrop-blur-xl">

          <div className="flex items-center gap-3 px-4 py-3">

            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() =>
                setOpen(
                  !open
                )
              }
              data-testid="mobile-menu-button"
            >

              {open ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}

            </Button>


            {/* Farm selector */}

            <div className="flex-1 max-w-xs">

              <Select
                value={
                  farmId || ""
                }
                onValueChange={
                  selectFarm
                }
              >

                <SelectTrigger
                  data-testid="farm-selector"
                  className="rounded-full bg-secondary border-none"
                >

                  <SelectValue placeholder="Select a farm" />

                </SelectTrigger>


                <SelectContent>

                  {farms.map(
                    (farmItem) => (

                      <SelectItem
                        key={
                          farmItem.id
                        }
                        value={
                          farmItem.id
                        }
                        data-testid={`farm-option-${farmItem.id}`}
                      >
                        {farmItem.name}
                      </SelectItem>

                    )
                  )}

                </SelectContent>

              </Select>

            </div>


            {/* Consultant return button */}

            {isClientFarm && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-full"
                onClick={() =>
                  navigate(
                    "/app/consultant"
                  )
                }
              >

                <BriefcaseBusiness className="h-4 w-4 sm:mr-2" />

                <span className="hidden sm:inline">
                  Consultant Dashboard
                </span>

              </Button>
            )}


            {/* Add farm */}

            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="add-farm-header-button"
              onClick={() =>
                navigate(
                  "/app/farm?new=1"
                )
              }
            >

              <Plus className="h-4 w-4 sm:mr-1" />

              <span className="hidden sm:inline">
                Farm
              </span>

            </Button>

          </div>


          {/* Client farm context banner */}

          {isClientFarm && farm && (

            <div className="border-t border-primary/10 bg-primary/[0.04] px-4 py-2">

              <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">

                <BriefcaseBusiness className="h-4 w-4 text-primary" />

                <span className="font-semibold text-[hsl(148,38%,16%)]">
                  Consultant workspace
                </span>

                <span className="text-muted-foreground">
                  Client:
                </span>

                <span className="font-medium text-[hsl(148,38%,16%)]">
                  {farm.client_name || "Client"}
                </span>

                <span className="text-muted-foreground">
                  ·
                </span>

                <span className="text-muted-foreground">
                  Farm:
                </span>

                <span className="font-medium text-[hsl(148,38%,16%)]">
                  {farm.name}
                </span>

              </div>

            </div>

          )}

        </header>


        {/* Page */}

        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto fade-up">

          {children}

        </main>

      </div>

    </div>
  );
}